import { mapAccess } from "../core/config.js";
import { debounce } from "../core/utils.js";
import { drawMask, findLayersBBox } from "../core/layers.js";
import { MapboxGLButtonControl } from "../core/controls.js";
import { domCache, setElementVisibility } from "./dom.js";
import { loadDataFromUrls, loadLayerFromData } from "./data.js";
import { createLegendRow } from "./legend.js";
import { hideLayer, showLayer } from "./visibility.js";

export function initMap(mapDivId, layerConfig, mapDivIdOverwrite) {
  let mapLayers = {};
  const containerDivId = mapDivId + "_container";
  const bounds = layerConfig.bound ?? [
    [-130, 40], // Southwest coordinates
    [-90, 70], // Northeast coordinates
  ];
  let styleLoaded = false;
  let headers = { "x-api-key": mapAccess.key };

  mapboxgl.accessToken = mapAccess.token;

  setElementVisibility("#" + containerDivId + " .lds-spinner", true);

  // Setup legend expand/compress action
  const iconElement = domCache.get("#" + containerDivId + " #hf-legend .icon");
  if (iconElement) {
    iconElement.setAttribute("role", "button");
    iconElement.setAttribute("aria-label", "Toggle legend visibility");
    iconElement.addEventListener('click', () => {
      const legendObj = domCache.get("#" + containerDivId + " #hf-legend");
      if (legendObj) {
        const hasCompress = legendObj.classList.contains("compress");
        legendObj.classList.toggle("compress", !hasCompress);
        legendObj.classList.toggle("expand", hasCompress);
      }
    });
  }

  // Click outside map container to close overlay
  const containerElement = document.getElementById(containerDivId);
  if (containerElement) {
    // Handler function to close overlay when interacting outside map container
    const handleCloseOverlay = (event) => {
      const isInsideContainer = event.target ? containerElement.contains(event.target) : false;

      // Only hide overlay if interaction is outside the map container
      if (!isInsideContainer) {
        const overlayMsg = domCache.get(`#${containerDivId} .overlay-msg`);
        if (overlayMsg) {
          overlayMsg.style.display = 'none';
        }
      }
    };

    // Debounced scroll handler to prevent performance issues
    const handleScrollDebounced = debounce(handleCloseOverlay, 150);

    // Add both click and scroll event listeners
    document.addEventListener('click', handleCloseOverlay);
    // window.addEventListener('scroll', handleScrollDebounced, { passive: true });
  }

  disableScroll(containerDivId);

  const legendObj = domCache.get("#" + containerDivId + " #hf-legend", true);
  if (legendObj) {
    if (layerConfig.closeLegend === true) {
      // Force deterministic closed state instead of relying on click-toggle behavior.
      legendObj.classList.add("compress");
      legendObj.classList.remove("expand");
    } else {
      legendObj.classList.remove("compress");
      legendObj.classList.add("expand");
    }
  }
  if (layerConfig.legendSupHeader) {
    const legendSuperHeader = domCache.get("#" + containerDivId + " #legend-super-header");
    if (legendSuperHeader) {
      legendSuperHeader.style.display = 'block';
      legendSuperHeader.textContent = layerConfig.legendSupHeader;
    }
  }

  return new Promise((resolve) => {
    let map = new mapboxgl.Map({
      container: mapDivIdOverwrite || mapDivId,
      style: layerConfig.showTopoLayer
        ? "mapbox://styles/mapbox/light-v10"
        : { version: 8, sources: {}, layers: [] },
      zoom: 6,
      center: [-114.6309516, 56.6293613],
      trackResize: true,
      attributionControl: false,
      preserveDrawingBuffer: true,
      // PERFORMANCE: Reduce map refresh rate to free up main thread
      refreshExpiredTiles: false,
      fadeDuration: 0, // Disable fade animations for better performance
      maxTileCacheSize: 50, // Limit tile cache to reduce memory
    });

    map.addControl(new mapboxgl.FullscreenControl());
    map.addControl(new mapboxgl.NavigationControl());
    map.addControl(
      new mapboxgl.ScaleControl({ maxWidth: 80, unit: "metric" }),
      "bottom-right"
    );

    // PERFORMANCE: Throttle data event handler
    let dataEventTimeout;
    map.on("data", function (data) {
      if (data.dataType === "source" && data.isSourceLoaded) {
        clearTimeout(dataEventTimeout);
        dataEventTimeout = setTimeout(() => {
          const spinner = domCache.get("#" + containerDivId + " .lds-spinner");
          if (spinner) spinner.style.display = 'none';
        }, 100);
      }
    });

    map.on("styledata", function () {
      if (!styleLoaded) {
        styleLoaded = true;
        switchLayers(containerDivId, layerConfig, map, mapLayers, headers);
        resolve({ mapLayers, map, headers });
      }
    });

    // PERFORMANCE: Throttle resize to prevent excessive redraws
    let resizeTimeout;
    map.on("idle", function () {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        map.resize();
      }, 250);
    });

    // OPTIMIZED: Debounced wheel event handler
    const containerElement = document.getElementById(containerDivId);
    if (containerElement) {
      const debouncedWheelHandler = debounce((event) => {
        const overlayMsg = domCache.get(`#${containerDivId} .overlay-msg`);
        if (!overlayMsg) return;

        if (event.ctrlKey) {
          event.preventDefault();
          if (!map.scrollZoom._enabled) map.scrollZoom.enable();
          overlayMsg.style.display = 'none';
        } else {
          if (map.scrollZoom._enabled) map.scrollZoom.disable();
          overlayMsg.style.display = 'block';
        }
      }, 100);

      containerElement.addEventListener("wheel", debouncedWheelHandler);
    }

    // PERFORMANCE: Reduce map rendering when not visible to free up main thread
    const mapContainer = document.getElementById(mapDivIdOverwrite || mapDivId);
    if (mapContainer && 'IntersectionObserver' in window) {
      let isMapVisible = true;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isMapVisible) {
            // Map is becoming visible - enable interactions
            isMapVisible = true;
            map.scrollZoom.enable();
            map.boxZoom.enable();
            map.dragPan.enable();
            map.dragRotate.enable();
            map.keyboard.enable();
            map.doubleClickZoom.enable();
            map.touchZoomRotate.enable();

            // Force a single render to update display
            map.resize();
          } else if (!entry.isIntersecting && isMapVisible) {
            // Map is becoming hidden - disable interactions and stop animations
            isMapVisible = false;
            map.scrollZoom.disable();
            map.boxZoom.disable();
            map.dragPan.disable();
            map.dragRotate.disable();
            map.keyboard.disable();
            map.doubleClickZoom.disable();
            map.touchZoomRotate.disable();

            // Stop any ongoing camera animations
            map.stop();

            // Remove render event listeners temporarily to reduce CPU
            map.off('render');
            map.off('move');
            map.off('zoom');
          }
        });
      }, {
        threshold: 0.1, // Trigger when 10% of map is visible
        rootMargin: '50px' // Start transition slightly before map enters/leaves viewport
      });

      observer.observe(mapContainer);
    }
  });
}

export function switchLayers(containerDivId, layerConfig, map, mapLayers, headers) {
  let mapBBox;
  setElementVisibility("#" + containerDivId + " .lds-spinner", true);

  const allNewLayers = (layerConfig.layers || []).reverse()
    .concat((layerConfig.aoi || []).reverse())
    .concat(layerConfig.baseLayers || []);

  // Hide existing layers not to be used
  Object.keys(mapLayers).forEach((layerId) => {
    if (
      !(layerConfig.layers || []).includes(layerId) &&
      !(layerConfig.aoi || []).map((x) => x).includes(layerId)
    ) {
      if (mapLayers[layerId] && mapLayers[layerId].layer) {
        hideLayer(containerDivId, map, layerId);
      }
      const legendRow = document.getElementById("legendRow" + layerId);
      if (legendRow) legendRow.style.display = 'none';
    }
  });

  // Check if layers are already loaded
  const pendingLayerRequests = [];

  allNewLayers.forEach((includeId) => {
    const id = includeId + ((layerConfig.aoi || []).includes(includeId) ? "" : "");
    const isAOI = (layerConfig.aoi || []).includes(includeId);

    if (map.getSource(id) && map.isSourceLoaded(id)) {
      // Already loaded, show layer and legend
      if (mapLayers[id] && mapLayers[id].linkedLayer) {
        mapLayers[id].linkedLayer.forEach((x) => {
          const input = domCache.get("#" + containerDivId + " #legendRow" + x + " input", true);
          if (input && input.checked) {
            showLayer(containerDivId, map, x);
          }
          const legendRow = document.getElementById("legendRow" + x);
          if (legendRow) legendRow.style.display = 'block';
        });
      } else {
        const input = domCache.get("#" + containerDivId + " #legendRow" + id + " input", true);
        if (input && input.checked) {
          showLayer(containerDivId, map, id);
        }
        const legendRow = document.getElementById("legendRow" + id);
        if (legendRow) legendRow.style.display = 'block';
      }
    } else {
      if (!layerConfig.legend?.hide?.includes(includeId)) {
        createLegendRow(
          containerDivId,
          [includeId],
          isAOI,
          layerConfig.useRadioButton,
          (layerConfig.baseLayers || []).includes(includeId),
          layerConfig.legendGroups || []
        );
      }

      pendingLayerRequests.push({
        id: includeId,
        isAOI,
        showLegend: !(layerConfig.baseLayers || []).includes(includeId),
      });
    }
  });

  const loadingLayerPromise = loadDataFromUrls(
    containerDivId,
    mapLayers,
    headers,
    pendingLayerRequests
  );

  loadingLayerPromise.then((loadedAPIData) => {
    let renderingTasks = [];
    let renderAOIs = [];

    allNewLayers.forEach((id) => {
      let data = loadedAPIData.find((x) => x.id == id);
      if (data) {
        const task = loadLayerFromData(
          containerDivId,
          data,
          map,
          mapLayers,
          layerConfig
        );

        if ((layerConfig.aoi || []).includes(id)) {
          renderAOIs.push(task);
        } else {
          renderingTasks.push(task);
        }
      }
    });

    // Handle AOI rendering
    Promise.all(renderAOIs).then((loadedLayers) => {

      const fitBoundsLayerIds = layerConfig.fitBoundsLayerIds ?? layerConfig.mask ?? layerConfig.aoi;
      const fallbackFitBoundsLayerIds = layerConfig.mask ?? layerConfig.aoi;
      findLayersBBox(mapLayers, fitBoundsLayerIds, layerConfig.bound).then((bbox) => {
        if (!bbox && fitBoundsLayerIds !== fallbackFitBoundsLayerIds) {
          findLayersBBox(mapLayers, fallbackFitBoundsLayerIds, layerConfig.bound).then(applyFitBounds);
          return;
        }

        applyFitBounds(bbox);
      });

      function applyFitBounds(bbox) {
        const defaultPadding = { padding: { top: 10, bottom: 10, left: 10, right: 15 } };
        const fitBoundsOptions = layerConfig.fitBoundPadding ?? defaultPadding;
        if (bbox) {
          mapBBox = bbox;
          map.fitBounds(bbox, fitBoundsOptions);
          const zoomToExtent = new MapboxGLButtonControl({
            className: "mapbox-gl-draw_polygon",
            title: "Zoom to Extent",
            eventHandler: function () {
              map.fitBounds(bbox, fitBoundsOptions);
            },
          });
          map.addControl(zoomToExtent);
        }

        const resizeButton = domCache.get("#" + containerDivId + " button#resize-map");
        if (resizeButton) {
          resizeButton.addEventListener('click', () => {
            map.resize();
            if (bbox) {
              map.fitBounds(bbox, fitBoundsOptions);
            }
          });
        }
      }
    });

    Promise.all(renderingTasks).then((loadedLayers) => {
      // Move AOI to front
      if (layerConfig.layers && layerConfig.layers.length > 0) {
        (layerConfig.aoi || []).forEach((x) => {
          map.moveLayer(x);
        });
      }

      // Handle masking
      if (layerConfig.mask?.length > 0) {
        if (map.getSource("world")) {
          if (layerConfig.layers) {
            map.moveLayer("world");
          }
        } else {
          const maskLayerGeom = [];
          Object.keys(mapLayers)
            .filter((x) => layerConfig.mask.includes(mapLayers[x].id))
            .forEach((x) =>
              mapLayers[x].layer.forEach((l) => maskLayerGeom.push(l))
            );
          drawMask(map, maskLayerGeom, layerConfig.maskColor || 'white', layerConfig.maskOpacity ?? 1);
        }
      }

      // Hide initial layers
      if (layerConfig.initialHideLayers) {
        layerConfig.initialHideLayers.forEach((layerId) => {

            // wait for map to finish loading
            map.once('idle', () => {
                setTimeout(() => {
                    hideLayer(containerDivId, map, layerId);
                    const input = domCache.get("#" + containerDivId + " #legendRow" + layerId + " input", true);
                    if (input) input.checked = false;
                }, 500); // we wait for 500ms since other debounce functions are earlier
            });

        });
      }

      // Setup radio button behavior
      if (layerConfig.useRadioButton) {
        setupRadioButtonBehavior(containerDivId, layerConfig, loadedLayers, mapLayers, map);
      }

      // Handle single color scheme
      if (layerConfig.hasOwnProperty("isSingleColorScheme")) {
        handleSingleColorScheme(containerDivId, layerConfig);
      }

      // Setup download link
      const downloadLink = domCache.get(`#${containerDivId} #downloadLink`);
      if (downloadLink) {
        downloadLink.addEventListener('click', function () {
          var img = map.getCanvas().toDataURL("image/png");
          this.href = img;
        });
      }

      // Force resize and zoom
      if (mapBBox) {
        map.fitBounds(mapBBox, layerConfig.fitBoundPadding ?? { padding: { top: 10, bottom: 10, left: 10, right: 15 } });
      }
    });
  });
}

// OPTIMIZED: Extracted radio button setup
export function setupRadioButtonBehavior(containerDivId, layerConfig, loadedLayers, mapLayers, map) {
  (layerConfig.layers || []).forEach((layerId, index) => {
    const checkboxCustom = domCache.get("#" + containerDivId + " #legendRow" + layerId + " .checkbox-custom", true);
    if (checkboxCustom) checkboxCustom.classList.add("radio");

    if (index > 0) {
      const input = domCache.get("#" + containerDivId + " #legendRow" + layerId + " input", true);
      if (input) input.checked = false;
      hideLayer(containerDivId, map, layerId);

      const subRow = domCache.get("#" + containerDivId + " #legendRow" + layerId + " .subRow", true);
      if (subRow) subRow.style.display = 'none';
    }
  });

  loadedLayers.flat().forEach((layerId) => {
    if (!(layerConfig.layers || []).includes(layerId)) {
      return;
    }

    const input = domCache.get("#" + containerDivId + " #legendRow" + layerId + " input", true);
    if (input) {
      input.addEventListener('change', function () {
        const isChecked = this.checked;
        if (!isChecked) return;

        const subRow = domCache.get("#" + containerDivId + " #legendRow" + layerId + " .subRow", true);
        if (subRow) subRow.style.display = 'block';

        const mapId = this.dataset.id;
        (layerConfig.layers || []).forEach((otherLayerId) => {
          if (otherLayerId != mapId) {
            const otherInput = domCache.get("#" + containerDivId + " #legendRow" + otherLayerId + " input", true);
            if (otherInput) otherInput.checked = false;

            const otherSubRow = domCache.get("#" + containerDivId + " #legendRow" + otherLayerId + " .subRow", true);
            if (otherSubRow) otherSubRow.style.display = 'none';

            hideLayer(containerDivId, map, otherLayerId);
          }
        });
      });
    }
  });
}

// OPTIMIZED: Extracted single color scheme handling
export function handleSingleColorScheme(containerDivId, layerConfig) {
  (layerConfig.layers || []).forEach((otherLayerId, index) => {
    if (index == 0) {
      const normalElement = domCache.get("#" + containerDivId + " .normal", true);
      if (normalElement) {
        normalElement.insertAdjacentHTML('afterbegin', '<div class="common"></div>');
      }

      const subRows = document.querySelectorAll("#legendRow" + otherLayerId + " .subRow");
      const fragment = document.createDocumentFragment();

      subRows.forEach((row) => {
        const clonedRow = row.cloneNode(true);
        if (layerConfig.isSingleColorScheme && layerConfig.isSingleColorScheme.isReverseOrder) {
          fragment.insertBefore(clonedRow, fragment.firstChild);
        } else {
          fragment.appendChild(clonedRow);
        }
      });

      const commonElement = domCache.get("#" + containerDivId + " .normal .common", true);
      if (commonElement) {
        commonElement.appendChild(fragment);
      }
    }

    const subRowsToRemove = document.querySelectorAll("#" + containerDivId + " #legendRow" + otherLayerId + " .subRow");
    subRowsToRemove.forEach(row => row.remove());
  });
}

export async function renderCompareMaps(mapSetting1, mapSetting2, containerId) {
  try {
    const currentResult = await initMap(
      mapSetting1.config.mapDivId,
      mapSetting1,
      mapSetting1.config.mapDivId + "_container"
    );
    const referenceResult = await initMap(
      mapSetting2.config.mapDivId,
      mapSetting2,
      mapSetting2.config.mapDivId + "_container"
    );

    const map = new mapboxgl.Compare(
      currentResult.map,
      referenceResult.map,
      containerId,
      {}
    );

    map.setSlider(map.currentPosition);
  } catch (error) {
    console.error('Error rendering compare maps:', error);
    throw error;
  }
}

export function disableScroll(containerId) {
  const overlayMsg = domCache.get(`#${containerId} .overlay-msg`);
  if (!overlayMsg) return;

  const debouncedScrollHandler = debounce((event) => {
    if (event.ctrlKey) {
      event.preventDefault();
      setTimeout(() => {
        overlayMsg.style.display = 'none';
      }, 2000);
    } else {
      setTimeout(() => {
        overlayMsg.style.display = 'block';
      }, 2000);
    }
  }, 100);

  overlayMsg.addEventListener("scroll", debouncedScrollHandler);
}
